/**
 * Gli indicatori di commesse e progetti (2.45, ADR-027, decisions of
 * 27/10/2026): the avanzamento, declared by the PM at each SAL or, without one,
 * from the hours; from it the earned value of the costs (EV) against the
 * planned (PV) and the spent (AC), SPI and CPI, the cost to finish (EAC,
 * automatic), the margin now and at the end, and the work accrued not yet
 * invoiced. Each with a semaforo from the table «Soglie degli indicatori».
 * Pure rules: the API gathers the figures, these decide.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { jobExpenses } from './job-budget.js';
import { fromCents, toCents } from './money.js';
import { isoDateSchema, optionalText } from './validation.js';
export const INDICATOR_THRESHOLDS_TABLE = 'soglie-indicatori';
export const INDICATOR_CODES = ['SPI', 'CPI', 'EAC', 'MARGINE', 'ORE', 'MATURATO', 'SAL'];
/** The standard thresholds, until the table says otherwise (27/10/2026). */
export const DEFAULT_INDICATOR_THRESHOLDS = {
    SPI: { green: 0.95, yellow: 0.85 },
    CPI: { green: 0.95, yellow: 0.85 },
    /** Cost to finish over the budget of the costs, %. */
    EAC: { green: 5, yellow: 15 },
    /** Points of margin lost against the planned one. */
    MARGINE: { green: 3, yellow: 10 },
    /** Points of hours used beyond the avanzamento. */
    ORE: { green: 5, yellow: 15 },
    /** Accrued not invoiced over the revenue, %. */
    MATURATO: { green: 10, yellow: 25 },
    /** Days since the last SAL. */
    SAL: { green: 30, yellow: 45 },
};
/** Higher is better for these; lower is better for the others. */
const HIGHER_IS_BETTER = new Set(['SPI', 'CPI']);
/** The thresholds from the entries of the table, the standard where one is missing. */
export function indicatorThresholds(entries) {
    const result = { ...DEFAULT_INDICATOR_THRESHOLDS };
    for (const code of INDICATOR_CODES) {
        const values = entries.find((entry) => entry.code === code)?.values;
        const green = Number(values?.green);
        const yellow = Number(values?.yellow);
        if (Number.isFinite(green) && Number.isFinite(yellow))
            result[code] = { green, yellow };
    }
    return result;
}
/** The semaforo of a value, or null when the value is not known. */
export function lightOf(code, value, thresholds) {
    if (value === null || !Number.isFinite(value))
        return null;
    const { green, yellow } = thresholds[code];
    if (HIGHER_IS_BETTER.has(code))
        return value >= green ? 'verde' : value >= yellow ? 'giallo' : 'rosso';
    return value <= green ? 'verde' : value <= yellow ? 'giallo' : 'rosso';
}
/** A SAL: the avanzamento declared by the PM on a day. */
export const progressSchema = z.object({
    on: isoDateSchema,
    percent: z
        .number()
        .min(0, { message: 'Tra 0 e 100' })
        .max(100, { message: 'Tra 0 e 100' })
        .multipleOf(0.5, { message: 'Al massimo mezzo punto' }),
    note: optionalText(1000),
});
/**
 * Why a SAL cannot be saved, or null: a lower avanzamento than the SAL before
 * it says why (27/10/2026), and a SAL is not in the future.
 */
export function progressProblem(body, previous, today) {
    if (body.on > today)
        return 'Il SAL non può essere nel futuro';
    if (previous && body.on >= previous.on && body.percent < previous.percent && !body.note) {
        return `L’avanzamento scende dal ${previous.percent} %: scrivi il perché nella nota`;
    }
    return null;
}
const round = (value, digits) => {
    const factor = 10 ** digits;
    return Math.round(value * factor) / factor;
};
const days = (from, to) => Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / 86_400_000);
/** The share of the time between start and end already gone, 0 to 1; null without dates. */
export function elapsedShare(startOn, endOn, today) {
    if (!startOn || !endOn)
        return null;
    if (today < startOn)
        return 0;
    if (today >= endOn)
        return 1;
    return (days(startOn, today) + 1) / (days(startOn, endOn) + 1);
}
/** The indicators of a commessa or progetto (2.45). */
export function jobIndicators(input, thresholds) {
    const planned = toCents(input.plannedHours);
    const worked = toCents(input.workedHours);
    const fromHours = planned > 0 ? round((worked / planned) * 100, 1) : null;
    const percent = input.progress ? input.progress.percent : fromHours;
    const share = percent === null ? null : Math.min(percent, 100) / 100;
    const salDays = input.progress ? days(input.progress.on, input.today) : null;
    const gap = percent !== null && fromHours !== null ? round(fromHours - percent, 1) : null;
    const bac = input.externalCosts.reduce((total, cost) => total + toCents(cost.amount), 0) +
        toCents(input.peoplePlanned);
    const elapsed = elapsedShare(input.startOn, input.endOn, input.today);
    const pv = elapsed === null
        ? null
        : input.externalCosts
            .filter((cost) => cost.on <= input.today)
            .reduce((total, cost) => total + toCents(cost.amount), 0) +
            Math.round(toCents(input.peoplePlanned) * elapsed);
    const ac = toCents(input.externalActual) + toCents(input.peopleActual);
    const ev = share === null ? null : Math.round(bac * share);
    const spi = ev !== null && pv ? round(ev / pv, 2) : null;
    const cpi = ev !== null && ac > 0 ? round(ev / ac, 2) : null;
    // Cost to finish: what is spent, plus the rest of the work at the efficiency so far.
    // Nothing spent yet says nothing on the costs (the invoices may come later): no estimate.
    const spent = ac > 0;
    const eac = ev === null || !spent ? null : cpi ? ac + Math.round((bac - ev) / (ev / ac)) : ac + (bac - ev);
    const overrun = eac !== null && bac > 0 ? round(((eac - bac) / bac) * 100, 1) : null;
    const revenue = input.revenue === null ? null : toCents(input.revenue);
    const marginOf = (income, cost) => income > 0 ? round(((income - cost) / income) * 100, 1) : null;
    const plannedMargin = revenue === null ? null : marginOf(revenue, bac);
    const marginAtEnd = revenue === null || eac === null ? null : marginOf(revenue, eac);
    const accrued = revenue === null || share === null ? null : Math.round(revenue * share);
    const marginNow = accrued === null || !spent ? null : marginOf(accrued, ac);
    const drop = (margin) => margin === null || plannedMargin === null ? null : round(plannedMargin - margin, 1);
    const unbilled = accrued === null ? null : accrued - toCents(input.invoiced);
    return {
        progress: {
            percent,
            source: input.progress ? 'sal' : percent === null ? null : 'ore',
            salOn: input.progress?.on ?? null,
            fromHours,
            salDays,
            // Never a SAL: rosso, as the oldest.
            salLight: lightOf('SAL', salDays ?? Number.POSITIVE_INFINITY, thresholds) ?? 'rosso',
        },
        hours: {
            planned: input.plannedHours,
            worked: input.workedHours,
            gap,
            light: lightOf('ORE', gap, thresholds),
        },
        spi: { value: spi, light: lightOf('SPI', spi, thresholds) },
        economics: {
            bac: fromCents(bac),
            pv: pv === null ? null : fromCents(pv),
            ac: fromCents(ac),
            ev: ev === null ? null : fromCents(ev),
            cpi: { value: cpi, light: lightOf('CPI', cpi, thresholds) },
            eac: {
                amount: eac === null ? null : fromCents(eac),
                overrun,
                light: lightOf('EAC', overrun, thresholds),
            },
            revenue: revenue === null ? null : fromCents(revenue),
            plannedMargin,
            marginAtEnd: {
                percent: marginAtEnd,
                light: lightOf('MARGINE', drop(marginAtEnd), thresholds),
            },
            marginNow: {
                percent: marginNow,
                accrued: accrued === null ? null : fromCents(accrued),
                light: lightOf('MARGINE', drop(marginNow), thresholds),
            },
            unbilled: {
                amount: unbilled === null ? null : fromCents(unbilled),
                invoiced: input.invoiced,
                light: lightOf('MATURATO', unbilled === null || !revenue ? null : round((unbilled / revenue) * 100, 1), thresholds),
            },
            expenses: jobExpenses(input.expenses?.active ?? '0.00', input.expenses?.passive ?? '0.00'),
        },
    };
}
/* --- Rischi e problemi (2.46) --------------------------------------------------- */
/** Rischio: may happen; problema: has happened; non conformità: a defect of quality. */
export const RISK_KINDS = ['rischio', 'problema', 'non_conformita'];
export const RISK_KIND_LABELS = {
    rischio: 'Rischio',
    problema: 'Problema',
    non_conformita: 'Non conformità',
};
export const RISK_AREAS = [
    'tempi',
    'costi',
    'qualita',
    'cliente',
    'fornitori',
    'persone',
    'altro',
];
export const RISK_AREA_LABELS = {
    tempi: 'Tempi',
    costi: 'Costi',
    qualita: 'Qualità',
    cliente: 'Cliente',
    fornitori: 'Fornitori',
    persone: 'Persone',
    altro: 'Altro',
};
export const RISK_STATES = ['aperto', 'in_mitigazione', 'chiuso'];
export const RISK_STATE_LABELS = {
    aperto: 'Aperto',
    in_mitigazione: 'In mitigazione',
    chiuso: 'Chiuso',
};
/** Probability and impact, 1 low to 3 high. */
const level = z.number().int().min(1).max(3);
export const riskSchema = z.object({
    kind: z.enum(RISK_KINDS),
    description: z.string().trim().min(1, { message: 'Descrivi il rischio' }).max(500),
    area: z.enum(RISK_AREAS),
    probability: level,
    impact: level,
    owner: optionalText(120),
    action: optionalText(1000),
    dueOn: isoDateSchema.nullable().default(null),
    state: z.enum(RISK_STATES).default('aperto'),
});
/** Gravità = probabilità × impatto: alta from 6, media from 3. */
export function riskSeverity(probability, impact) {
    const score = probability * impact;
    return score >= 6 ? 'alta' : score >= 3 ? 'media' : 'bassa';
}
/** An open item past its date. */
export function riskOverdue(risk, today) {
    return risk.state !== 'chiuso' && risk.dueOn !== null && risk.dueOn < today;
}
/**
 * The semaforo of the register (27/10/2026): rosso with an open item past its
 * date, giallo with a grave one open in time, verde otherwise.
 */
export function risksSummary(risks, today) {
    const open = risks.filter((risk) => risk.state !== 'chiuso');
    const grave = open.filter((risk) => riskSeverity(risk.probability, risk.impact) === 'alta').length;
    const overdue = open.filter((risk) => riskOverdue(risk, today)).length;
    return {
        open: open.length,
        grave,
        overdue,
        light: overdue > 0 ? 'rosso' : grave > 0 ? 'giallo' : 'verde',
    };
}
/**
 * The quality indicators proposed for a commessa (27/10/2026): added with one
 * click, then changed or removed like any other.
 */
export const QUALITY_INDICATORS = [
    {
        name: 'Soddisfazione dei partecipanti',
        method: 'Questionario di fine modulo, media da 1 a 5',
        baseline: '0',
        target: '4.2',
    },
    {
        name: 'Deliverable accettati al primo invio',
        method: 'Accettati senza correzioni / consegnati, %',
        baseline: '0',
        target: '90',
    },
    {
        name: 'Milestone nei tempi',
        method: 'Milestone entro la data / milestone previste, %',
        baseline: '0',
        target: '100',
    },
];
//# sourceMappingURL=job-indicators.js.map